import type { Empresa } from '../types';
import { getMonedaPorPais } from './constants';

/** Minimal empresa slice for currency gating — callers need not supply full ConfiguracionEmpresa. */
export type EmpresaMonedaContext = {
  pais?: string | null;
  configuracion?: Partial<NonNullable<Empresa['configuracion']>> & {
    monedas_aceptadas?: string[];
  };
} | null | undefined;

const MONEDAS_VE_DEFAULT = ['USD', 'VES', 'COP'] as const;

/** ISO-ish country code for currency gating (VE vs everyone else). */
export function normalizePaisEmpresa(pais?: string | null): string {
  const raw = String(pais || '').trim().toUpperCase();
  if (!raw) return '';
  if (raw.length === 2) return raw;
  const byName: Record<string, string> = {
    COLOMBIA: 'CO',
    VENEZUELA: 'VE',
    MÉXICO: 'MX',
    MEXICO: 'MX',
    'ESTADOS UNIDOS': 'US',
  };
  return byName[raw] || raw.slice(0, 2);
}

/** Multi-currency is a Venezuela-only product capability. */
export function isEmpresaVenezuela(empresa: EmpresaMonedaContext): boolean {
  return normalizePaisEmpresa(empresa?.pais) === 'VE';
}

/**
 * True when the empresa may show multi-currency UI (tabs, selectors, FX).
 * Single gate — all currency-touching branches should call this.
 */
export function isEmpresaMultiMoneda(empresa: EmpresaMonedaContext): boolean {
  if (!isEmpresaVenezuela(empresa)) return false;
  return getMonedasAceptadasVenezuela(empresa).length > 1;
}

function normalizeMonedaCode(moneda?: string | null): string {
  return String(moneda || '').trim().toUpperCase();
}

function getMonedasAceptadasVenezuela(empresa: EmpresaMonedaContext): string[] {
  const custom = (empresa?.configuracion as { monedas_aceptadas?: string[] } | undefined)
    ?.monedas_aceptadas;
  if (Array.isArray(custom) && custom.length > 0) {
    const normalized = custom.map((m) => normalizeMonedaCode(m)).filter(Boolean);
    if (normalized.length > 0) return normalized;
  }
  return [...MONEDAS_VE_DEFAULT];
}

function monedaFromConfig(empresa?: EmpresaMonedaContext): string {
  const cfg = empresa?.configuracion as
    | {
        moneda?: string;
        sistema?: { moneda?: string };
      }
    | undefined;
  return normalizeMonedaCode(cfg?.sistema?.moneda || cfg?.moneda);
}

/** Resolve display/settlement currency from country + the moneda the merchant selected. */
export function getEmpresaMoneda(empresa?: EmpresaMonedaContext): string {
  const saved = monedaFromConfig(empresa);
  if (isEmpresaVenezuela(empresa)) {
    return saved || 'USD';
  }
  if (saved) return saved;
  const pais = normalizePaisEmpresa(empresa?.pais);
  if (pais) return getMonedaPorPais(pais);
  return 'COP';
}

/** Accepted checkout / cierre currencies. VE: multi; others: the selected operating currency. */
export function getMonedasAceptadas(empresa?: EmpresaMonedaContext): string[] {
  if (isEmpresaVenezuela(empresa)) return getMonedasAceptadasVenezuela(empresa);
  return [getEmpresaMoneda(empresa)];
}

/** Moneda that must be stored on new rows for this empresa (until per-transaction picker exists). */
export function monedaObligatoriaEmpresa(empresa: EmpresaMonedaContext): string {
  return getEmpresaMoneda(empresa);
}

/**
 * Validates moneda at write time. VE: must be in getMonedasAceptadas.
 * Other countries: the operating currency the merchant selected (country default if unset).
 */
export function assertMonedaPermitidaParaEmpresa(
  empresa: EmpresaMonedaContext,
  moneda?: string | null,
): string {
  if (isEmpresaVenezuela(empresa)) {
    const allowed = new Set(getMonedasAceptadas(empresa));
    const chosen = normalizeMonedaCode(moneda) || monedaObligatoriaEmpresa(empresa);
    if (!allowed.has(chosen)) {
      throw new Error(`La moneda ${chosen} no está habilitada para esta empresa.`);
    }
    return chosen;
  }
  const operating = getEmpresaMoneda(empresa);
  const chosen = normalizeMonedaCode(moneda) || operating;
  if (chosen && chosen !== operating) {
    throw new Error(`Esta empresa opera en ${operating}.`);
  }
  return operating;
}

/** Strip multi-moneda config when persisting non-VE empresas (prevents JSON-only bypass). */
export function monedasAceptadasParaPersistir(
  empresa: EmpresaMonedaContext,
  monedas?: string[] | null,
): string[] | undefined {
  if (!isEmpresaVenezuela(empresa)) return undefined;
  if (!Array.isArray(monedas) || monedas.length === 0) return undefined;
  const normalized = monedas.map((m) => normalizeMonedaCode(m)).filter(Boolean);
  return normalized.length > 0 ? normalized : undefined;
}

export function labelMontoMoneda(moneda: string): string {
  return `Monto (${moneda})`;
}
